import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import Modal from '../../components/modal'
import Input from '../../components/input'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import FileUpload from '../../components/file-upload'
import useI18n from '../../i18n/useI18n'

const StartContractModal = ({ isOpen, onClose, agent, onSuccess }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()

  const [formData, setFormData] = useState({
    contractStartDate: '',
    contractEndDate: '',
    licenseNumber: '',
    licenseExpiryDate: '',
    commissionRate: ''
  })

  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [contractFile, setContractFile] = useState(null)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0]
      setFormData({
        contractStartDate: today,
        contractEndDate: '',
        licenseNumber: '',
        licenseExpiryDate: '',
        commissionRate: ''
      })
      setValidationErrors({})
      setSubmitError('')
      setContractFile(null)
    }
  }, [isOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setSubmitError('')

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const errors = {}
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Contract Start Date validation
    if (!formData.contractStartDate || !formData.contractStartDate.trim()) {
      errors.contractStartDate = t('AGENTS_CONTRACT_VALIDATION_START_DATE_REQUIRED')
    } else {
      const startDate = new Date(formData.contractStartDate)
      startDate.setHours(0, 0, 0, 0)
      if (startDate < today) {
        errors.contractStartDate = t('AGENTS_CONTRACT_VALIDATION_START_DATE_NOT_PAST')
      }
    }

    // Contract End Date validation
    if (!formData.contractEndDate || !formData.contractEndDate.trim()) {
      errors.contractEndDate = t('AGENTS_CONTRACT_VALIDATION_END_DATE_REQUIRED')
    } else if (formData.contractStartDate) {
      const startDate = new Date(formData.contractStartDate)
      const endDate = new Date(formData.contractEndDate)
      if (endDate <= startDate) {
        errors.contractEndDate = t('AGENTS_CONTRACT_VALIDATION_END_DATE_AFTER_START')
      }
    }

    // License Number validation
    if (!formData.licenseNumber || !formData.licenseNumber.trim()) {
      errors.licenseNumber = t('AGENTS_CONTRACT_VALIDATION_LICENSE_NUMBER_REQUIRED')
    }

    // License Expiry Date validation
    if (!formData.licenseExpiryDate || !formData.licenseExpiryDate.trim()) {
      errors.licenseExpiryDate = t('AGENTS_CONTRACT_VALIDATION_LICENSE_EXPIRY_REQUIRED')
    } else if (formData.contractStartDate) {
      const startDate = new Date(formData.contractStartDate)
      const expiryDate = new Date(formData.licenseExpiryDate)
      if (expiryDate < startDate) {
        errors.licenseExpiryDate = t('AGENTS_CONTRACT_VALIDATION_LICENSE_EXPIRY_AFTER_START')
      }
    }

    // Commission Rate validation (optional but if provided, must be valid)
    if (formData.commissionRate && formData.commissionRate.trim()) {
      const commissionRate = parseFloat(formData.commissionRate)
      if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
        errors.commissionRate = t('AGENTS_FORM_VALIDATION_COMMISSION_RATE_INVALID')
      }
    }

    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      setSubmitError(t('AGENTS_CONTRACT_VALIDATION_GENERIC'))
      return false
    }

    return true
  }

  const buildSubmitData = () => {
    const submitData = {
      contractStartDate: formData.contractStartDate.trim(),
      contractEndDate: formData.contractEndDate.trim(),
      licenseNumber: formData.licenseNumber.trim(),
      licenseExpiryDate: formData.licenseExpiryDate.trim()
    }

    // Add commission rate if provided
    if (formData.commissionRate && formData.commissionRate.trim()) {
      submitData.commissionRate = parseFloat(formData.commissionRate)
    }

    return submitData
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) {
      return
    }

    if (!agent?.id) {
      setSubmitError(t('AGENTS_CONTRACT_ERROR_NO_AGENT'))
      return
    }

    const payload = buildSubmitData()
    setIsSubmitting(true)

    dispatch(
      ApiCallActions.Post({
        url: `agents/${agent.id}/start-contract`,
        data: payload,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          if (contractFile) {
            const fileFormData = new FormData()
            fileFormData.append('file', contractFile)

            dispatch(
              ApiCallActions.Post({
                url: `files/agents/${agent.id}/contract`,
                axiosOptions: {
                  data: fileFormData,
                  headers: { 'Content-Type': 'multipart/form-data' }
                },
                showAlertOnError: true,
                showLoading: true,
                onSuccess: () => {
                  general.messageSuccess(t('AGENTS_CONTRACT_START_SUCCESS'))
                  setIsSubmitting(false)
                  onClose()
                  if (onSuccess) {
                    onSuccess()
                  }
                },
                onError: ({ errorMessage }) => {
                  setSubmitError(errorMessage || t('AGENTS_CONTRACT_START_ERROR'))
                  setIsSubmitting(false)
                }
              })
            )
          } else {
            general.messageSuccess(t('AGENTS_CONTRACT_START_SUCCESS'))
            setIsSubmitting(false)
            onClose()
            if (onSuccess) {
              onSuccess()
            }
          }
        },
        onError: ({ errorMessage }) => {
          setSubmitError(errorMessage || t('AGENTS_CONTRACT_START_ERROR'))
          setIsSubmitting(false)
        }
      })
    )
  }

  const getAgentName = () => {
    if (!agent) return ''
    if (agent.companyName) return agent.companyName
    if (agent.name) return agent.name
    if (agent.fullName) return agent.fullName
    const firstName = agent?.user?.firstName || ''
    const lastName = agent?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()
    return combined || agent?.user?.email || ''
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('AGENTS_CONTRACT_START_TITLE')}
      subtitle={getAgentName() || t('AGENTS_CONTRACT_START_SUBTITLE')}
      icon="zmdi-file-text"
      size="medium"
    >
      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('AGENTS_CONTRACT_FIELD_START_DATE')} <span className="text-danger">*</span></label>
              <Input
                type="date"
                name="contractStartDate"
                value={formData.contractStartDate}
                onChange={handleChange}
                required
              />
              {validationErrors.contractStartDate && (
                <small className="text-danger">{validationErrors.contractStartDate}</small>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('AGENTS_CONTRACT_FIELD_END_DATE')} <span className="text-danger">*</span></label>
              <Input
                type="date"
                name="contractEndDate"
                value={formData.contractEndDate}
                onChange={handleChange}
                min={formData.contractStartDate || undefined}
                required
              />
              {validationErrors.contractEndDate && (
                <small className="text-danger">{validationErrors.contractEndDate}</small>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('AGENTS_CONTRACT_FIELD_LICENSE_NUMBER')} <span className="text-danger">*</span></label>
              <Input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                placeholder={t('AGENTS_CONTRACT_FIELD_LICENSE_NUMBER_PLACEHOLDER')}
                required
              />
              {validationErrors.licenseNumber && (
                <small className="text-danger">{validationErrors.licenseNumber}</small>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('AGENTS_CONTRACT_FIELD_LICENSE_EXPIRY_DATE')} <span className="text-danger">*</span></label>
              <Input
                type="date"
                name="licenseExpiryDate"
                value={formData.licenseExpiryDate}
                onChange={handleChange}
                min={formData.contractStartDate || undefined}
                required
              />
              {validationErrors.licenseExpiryDate && (
                <small className="text-danger">{validationErrors.licenseExpiryDate}</small>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('AGENTS_CONTRACT_FIELD_COMMISSION_RATE')}</label>
              <Input
                type="number"
                name="commissionRate"
                value={formData.commissionRate}
                onChange={handleChange}
                placeholder={t('AGENTS_CONTRACT_FIELD_COMMISSION_RATE_PLACEHOLDER')}
                min="0"
                max="100"
                step="0.1"
              />
              {validationErrors.commissionRate && (
                <small className="text-danger">{validationErrors.commissionRate}</small>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group m-t-10">
              <FileUpload
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                label={t('AGENTS_CONTRACT_FIELD_CONTRACT_FILE')}
                showPreview={false}
                onFileSelect={(file) => setContractFile(file)}
                disabled={isSubmitting}
              />
              <small className="text-muted d-block m-t-5">
                {t('AGENTS_CONTRACT_FIELD_CONTRACT_FILE_HELP')}
              </small>
            </div>
          </div>
        </div>

        <div className="alert alert-info m-t-20" role="alert">
          <i className="zmdi zmdi-info-outline m-r-5"></i>
          {t('AGENTS_CONTRACT_NOTE_EXISTING_CONTRACT')}
        </div>

        <div className="row m-t-30">
          <div className="col-md-12 text-right"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <button
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              type="button"
              className="btn btn-default btn-round m-r-10"
              onClick={onClose}
              disabled={isSubmitting}
            >
              <i className="zmdi zmdi-close m-r-5"></i>
              {t('AGENTS_CONTRACT_BUTTON_CANCEL')}
            </button>
            <button
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              type="submit"
              className="btn btn-primary btn-round"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                  {t('AGENTS_CONTRACT_BUTTON_STARTING')}
                </>
              ) : (
                <>
                  <i className="zmdi zmdi-check m-r-5"></i>
                  {t('AGENTS_CONTRACT_BUTTON_START')}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default StartContractModal

